"use client";

import { CommandGroup } from "@universe/ui/components/command";
import { useParams, useRouter } from "next/navigation";
import { useMemo, useState } from "react";

import type { ProductType } from "@/constants";
import { useActiveAccounts } from "@/hooks/useActiveAccounts";
import useCurrencyFormatter from "@/hooks/useCurrencyFormatter";
import { useGlobalData } from "@/providers";

import { AccountSelectItem } from "./account-select-item";
import { AccountSelectPopover } from "./account-select-popover";

type ActiveAccountsSelectProps = {
  className?: string;
  placeholder?: string;
  currentAccountId?: string;
  onSelect?: (accountId: string) => void;
  showFlag?: boolean;
  flagSize?: string;
  popoverAlign?: "start" | "center" | "end";
  /** When true, only show the name (not the currency code) in the trigger */
  showNameOnly?: boolean;
  /** When true, also include available accounts (those without balance yet) */
  includeAvailable?: boolean;
};

type ActiveAccountItem = {
  value: string;
  label: string;
  name: string;
  searchIndex: string;
  balance: number;
  type: ProductType;
  isAvailable?: boolean;
};

/**
 * Account select component that shows ALL active accounts from useActiveAccounts.
 * Unlike BalanceAccountsSelect, this includes accounts with 0 balance.
 * Optionally includes available accounts (settlement methods without balance yet).
 */
export function ActiveAccountsSelect({
  className,
  placeholder,
  currentAccountId: _currentAccountId,
  onSelect,
  showFlag = false,
  flagSize = "28px",
  popoverAlign = "start",
  showNameOnly = false,
  includeAvailable = true,
}: ActiveAccountsSelectProps) {
  const [open, setOpen] = useState(false);
  const { productId } = useParams<{ productId: string }>();
  const router = useRouter();

  const {
    accounts,
    availableAccounts,
    isLoading: isActiveAccountsLoading,
  } = useActiveAccounts();
  const { products, isLoadingProducts } = useGlobalData();
  const { formatCurrency } = useCurrencyFormatter();

  const isLoading = isActiveAccountsLoading || isLoadingProducts;

  // Use productId from URL as the source of truth, with local state for user selections
  const [localValue, setLocalValue] = useState("");
  // Derive value: prefer productId from URL, fallback to local selection
  const value = productId || localValue;

  // Build list of active accounts (includes 0 balance accounts)
  const activeAccountsList = useMemo(() => {
    const items: ActiveAccountItem[] = [];

    accounts.forEach((account) => {
      const productInfo = products?.find((p) => p.code === account.code);

      items.push({
        value: account.code,
        label: formatCurrency(account.balance, account.code) ?? account.code,
        name: productInfo?.name || account.code,
        searchIndex: `${account.code}-${productInfo?.name || account.code}`,
        balance: account.balance,
        type: account.type,
        isAvailable: false,
      });
    });

    return items;
  }, [accounts, products, formatCurrency]);

  // Build list of available accounts (no balance yet)
  const availableAccountsList = useMemo(() => {
    if (!includeAvailable) return [];

    const items: ActiveAccountItem[] = [];

    availableAccounts.forEach((account) => {
      const productInfo = products?.find((p) => p.code === account.code);

      items.push({
        value: account.code,
        label: account.code,
        name: productInfo?.name || account.code,
        searchIndex: `${account.code}-${productInfo?.name || account.code}`,
        balance: 0,
        type: account.type,
        isAvailable: true,
      });
    });

    return items;
  }, [availableAccounts, products, includeAvailable]);

  // Find selected account from either list
  let selectedAccount = activeAccountsList.find(
    (account) => account.value === value
  );

  // Check available accounts if not found in active
  if (!selectedAccount) {
    selectedAccount = availableAccountsList.find(
      (account) => account.value === value
    );
  }

  // If still not found and we're on account detail page, look it up from products
  if (!selectedAccount && value && productId) {
    const productInfo = products?.find((p) => p.code === value);
    if (productInfo) {
      selectedAccount = {
        value: value,
        label: value,
        name: productInfo.name || value,
        searchIndex: `${value}-${productInfo.name || value}`,
        balance: 0,
        type: productInfo.resourceType as ProductType,
        isAvailable: true,
      };
    }
  }

  const handleSelect = (accountId: string) => {
    // Close popover first to prevent re-render issues during parent state updates
    setOpen(false);
    setLocalValue(accountId);

    // Navigate to account detail page
    if (productId) {
      router.push(`/accounts/${accountId}`);
    }

    onSelect?.(accountId);
  };

  const hasActiveAccounts = activeAccountsList.length > 0;
  const hasAvailableAccounts = availableAccountsList.length > 0;

  return (
    <AccountSelectPopover
      open={open}
      onOpenChange={setOpen}
      isLoading={isLoading}
      className={className}
      popoverAlign={popoverAlign}
      selectedValue={selectedAccount?.value}
      selectedName={selectedAccount?.name}
      showFlag={showFlag}
      flagSize={flagSize}
      placeholder={placeholder}
      showNameOnly={showNameOnly}
    >
      {hasActiveAccounts && (
        <CommandGroup>
          {activeAccountsList.map((account) => (
            <AccountSelectItem
              key={account.searchIndex}
              value={account.value}
              label={account.label}
              name={account.name}
              searchIndex={account.searchIndex}
              isSelected={value === account.value}
              onSelect={() => handleSelect(account.value)}
            />
          ))}
        </CommandGroup>
      )}
      {hasAvailableAccounts && (
        <CommandGroup>
          {availableAccountsList.map((account) => (
            <AccountSelectItem
              key={account.searchIndex}
              value={account.value}
              label={account.label}
              name={account.name}
              searchIndex={account.searchIndex}
              isSelected={value === account.value}
              onSelect={() => handleSelect(account.value)}
            />
          ))}
        </CommandGroup>
      )}
    </AccountSelectPopover>
  );
}
